/**
 * CrmLeads — V2.1.0 (Feb 2026, Phase 1)
 *
 * The new CRM section — pre-CV leads live here. Two-surface model:
 * candidates keep their existing Kanban; CRM is the funnel that feeds
 * it via CV upload OR self-registration.
 *
 * Reuses design guidelines: Cabinet Grotesk + sharp edges + Cobalt +
 * priority dot vocabulary. Density toggle, 5 view tabs, source-batch
 * filter chips, and a compact row list with disposition action.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { logError } from "@/lib/logger";
import { toast } from "sonner";
import {
    Plus, MagnifyingGlass, ArrowRight, Users, PhoneCall, Snowflake,
    CheckCircle, ClockCounterClockwise, Package, X, ArrowLeft, Sparkle,
    UserSwitch, ListChecks, ArrowsCounterClockwise,
} from "@phosphor-icons/react";
import DispositionModal from "@/components/DispositionModal";
import CrmLeadDrawer from "@/components/CrmLeadDrawer";
import BulkActionsBar from "@/components/BulkActionsBar";
import BulkReassignModal from "@/components/BulkReassignModal";
import BulkDispositionModal from "@/components/BulkDispositionModal";
import BulkWhatsAppModal from "@/components/BulkWhatsAppModal";
import { TodayInbox } from "@/pages/Today";

const TABS = [
    { key: "all",        label: "All",         icon: Users },
    { key: "mine",       label: "My assigned", icon: CheckCircle },
    { key: "available",  label: "Available",   icon: Package },
    { key: "callbacks",  label: "Callbacks",   icon: ClockCounterClockwise },
    { key: "cold_out",   label: "Cold out",    icon: Snowflake },
    // V2.1.1 iter184 — Kanban auto-rejects that were recycled to CRM.
    { key: "recycles",   label: "Recycles",    icon: ArrowsCounterClockwise },
];

const SOURCE_BATCHES = [
    { key: "manual_recruiter", label: "Manual" },
    { key: "v1_mongo",         label: "Legacy v1" },
    { key: "csv_import",       label: "CSV" },
    { key: "partner_form",     label: "Partner" },
    { key: "fb_ad",            label: "Ad" },
];

const DOT_COLOR_BY_DISPOSITION = {
    interested:          "bg-emerald-500",
    callback_scheduled:  "bg-cobalt",
    not_reachable:       "bg-amber-500",
    referral_pending:    "bg-cobalt",
    offer_pending_elsewhere: "bg-amber-500",
    salary_mismatch:     "bg-amber-400",
    location_mismatch:   "bg-amber-400",
    profile_mismatch:    "bg-amber-400",
    not_interested:      "bg-rose-500",
    cold_recycled:       "bg-neutral-400",
    hired_elsewhere:     "bg-neutral-500",
    wrong_contact:       "bg-neutral-400",
    duplicate:           "bg-neutral-400",
    dnc:                 "bg-rose-800",
};

function PriorityDot({ code }) {
    const cls = DOT_COLOR_BY_DISPOSITION[code] || "bg-neutral-300";
    return <span className={`inline-block w-2.5 h-2.5 rounded-full ${cls}`} />;
}

function ActionChip({ lead }) {
    const disp = lead.current_disposition;
    if (disp?.code === "callback_scheduled" && disp.next_action_at) {
        const when = new Date(disp.next_action_at);
        const label = isNaN(when) ? "Callback due" : `Callback ${when.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}`;
        return <span className="text-[10px] font-bold px-2 py-0.5 border border-cobalt text-cobalt">{label}</span>;
    }
    if (disp?.code) {
        return <span className="text-[10px] font-bold px-2 py-0.5 border border-neutral-400 text-neutral-700 capitalize">{disp.code.replace(/_/g, " ")}</span>;
    }
    if (!lead.owner_id) {
        return <span className="text-[10px] font-bold px-2 py-0.5 border border-cobalt text-cobalt">Unassigned</span>;
    }
    return <span className="text-[10px] font-bold px-2 py-0.5 border border-neutral-300 text-neutral-500">No touch yet</span>;
}

function _relTime(iso) {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    if (isNaN(t)) return "";
    const diff = Date.now() - t;
    const m = Math.round(diff / 60000);
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    return `${d}d ago`;
}

function AddLeadForm({ onCreated, onCancel }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [city, setCity] = useState("");
    const [title, setTitle] = useState("");
    const [skills, setSkills] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const [dupWarn, setDupWarn] = useState(null);

    async function submit() {
        if (!name.trim() || (!email && !phone)) {
            toast.error("Name + email or phone required");
            return;
        }
        setSaving(true); setDupWarn(null);
        try {
            const r = await api.post("/crm/leads", {
                name: name.trim(),
                email: email.trim() || null,
                phone: phone.trim() || null,
                city: city.trim() || null,
                current_title: title.trim() || null,
                skills: skills ? skills.split(",").map((s) => s.trim()).filter(Boolean) : [],
                notes: notes.trim() || null,
            });
            if (r.data?.dup_candidate) setDupWarn(r.data.dup_candidate);
            toast.success("Lead added to CRM");
            onCreated?.(r.data.lead);
        } catch (e) {
            logError("Add lead failed:", e);
            toast.error(e?.response?.data?.detail || "Couldn't add lead");
        } finally { setSaving(false); }
    }

    return (
        <div className="border-2 border-ink bg-white p-5 space-y-3" data-testid="crm-add-form">
            <div className="flex items-center justify-between">
                <h3 className="font-heading font-black text-lg">Add lead</h3>
                <button onClick={onCancel} className="p-1 hover:bg-neutral-100" data-testid="crm-add-cancel"><X size={16} /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label><span className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Name *</span>
                    <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full border-2 border-ink px-3 py-2 text-sm" data-testid="crm-add-name" />
                </label>
                <label><span className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Email</span>
                    <input value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full border-2 border-ink px-3 py-2 text-sm" data-testid="crm-add-email" />
                </label>
                <label><span className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Phone</span>
                    <input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1 w-full border-2 border-ink px-3 py-2 text-sm" data-testid="crm-add-phone" />
                </label>
                <label><span className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">City</span>
                    <input value={city} onChange={(e) => setCity(e.target.value)} className="mt-1 w-full border-2 border-ink px-3 py-2 text-sm" data-testid="crm-add-city" />
                </label>
                <label className="sm:col-span-2"><span className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Current title</span>
                    <input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 w-full border-2 border-ink px-3 py-2 text-sm" data-testid="crm-add-title" />
                </label>
                <label className="sm:col-span-2"><span className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Skills (comma separated)</span>
                    <input value={skills} onChange={(e) => setSkills(e.target.value)} placeholder="React, Node.js, Sales" className="mt-1 w-full border-2 border-ink px-3 py-2 text-sm" data-testid="crm-add-skills" />
                </label>
                <label className="sm:col-span-2"><span className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Notes</span>
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1 w-full border-2 border-ink px-3 py-2 text-sm" data-testid="crm-add-notes" />
                </label>
            </div>
            {dupWarn && (
                <div className="border-2 border-amber-500 bg-amber-50 px-3 py-2 text-xs">
                    Heads-up: <b>{dupWarn.name}</b> may already exist in the pool. Consider linking or promoting instead of duplicating.
                </div>
            )}
            <div className="flex justify-end gap-2">
                <button onClick={onCancel} className="border-2 border-ink px-4 py-2 text-xs font-bold uppercase">Cancel</button>
                <button onClick={submit} disabled={saving} className="bg-ink text-white px-4 py-2 text-xs font-bold uppercase disabled:opacity-40" data-testid="crm-add-submit">
                    {saving ? "Saving…" : "Add lead"}
                </button>
            </div>
        </div>
    );
}

export default function CrmLeads() {
    const [searchParams, setSearchParams] = useSearchParams();
    // V2.1.1 iter167 — top-level section: "today" (inbox-zero landing,
    // default) | "pool" (raw leads list). Persisted in URL so nav from
    // outside can deep-link. Legacy /today route → /crm?section=today.
    const section = searchParams.get("section") === "pool" ? "pool" : "today";
    const setSection = (s) => {
        const p = new URLSearchParams(searchParams);
        p.set("section", s);
        setSearchParams(p, { replace: true });
    };

    const [view, setView] = useState("all");
    const [sourceBatch, setSourceBatch] = useState("");
    const [q, setQ] = useState("");
    const [debouncedQ, setDebouncedQ] = useState("");
    const [density, setDensity] = useState("compact"); // compact | cozy
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [showAdd, setShowAdd] = useState(false);
    const [dispTarget, setDispTarget] = useState(null); // {id}
    const [openLeadId, setOpenLeadId] = useState(null);
    // V2.1.1 iter183 — bulk selection state. `selectedIds` is a Set for
    // O(1) toggle. `bulkModal` picks which modal (reassign|disposition)
    // is open; null = closed.
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [bulkModal, setBulkModal] = useState(null); // 'reassign' | 'disposition' | null

    useEffect(() => {
        const t = setTimeout(() => setDebouncedQ(q), 300);
        return () => clearTimeout(t);
    }, [q]);

    const fetchList = useCallback(() => {
        setLoading(true); setError("");
        const params = new URLSearchParams({ view, limit: "50" });
        if (sourceBatch) params.set("source_batch", sourceBatch);
        if (debouncedQ) params.set("q", debouncedQ);
        api.get(`/crm/leads?${params.toString()}`)
            .then((r) => { setRows(r.data.rows || []); setTotal(r.data.total || 0); })
            .catch((e) => { logError("CRM list failed:", e); setError(e?.response?.data?.detail || "Couldn't load leads"); })
            .finally(() => setLoading(false));
    }, [view, sourceBatch, debouncedQ]);

    useEffect(() => { fetchList(); }, [fetchList]);

    const fetchSummary = useCallback(() => {
        api.get("/crm/summary").then((r) => setSummary(r.data)).catch(() => {});
    }, []);

    useEffect(() => { fetchSummary(); }, [fetchSummary, rows.length]);

    // V2.1.1 iter183 — clear stale selections when the row set changes
    // (a new tab/search may have hidden previously-selected leads).
    useEffect(() => {
        setSelectedIds((prev) => {
            if (prev.size === 0) return prev;
            const visible = new Set(rows.map((r) => r.id));
            const next = new Set();
            prev.forEach((id) => { if (visible.has(id)) next.add(id); });
            return next.size === prev.size ? prev : next;
        });
    }, [rows]);

    const toggleSelect = useCallback((id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);

    const toggleSelectAll = useCallback(() => {
        setSelectedIds((prev) => (
            prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.id))
        ));
    }, [rows]);

    const rowH = density === "compact" ? "py-2" : "py-3";

    return (
        <div className="max-w-6xl mx-auto px-6 py-8" data-testid="crm-page">
            <Link
                to="/dashboard"
                className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-ink font-bold uppercase tracking-widest mb-6"
                data-testid="crm-back-link"
            >
                <ArrowLeft size={12} weight="bold" /> Dashboard
            </Link>

            <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Pre-CV pipeline</p>
                    <h1 className="font-heading font-black text-3xl md:text-4xl leading-none mt-1">CRM</h1>
                    <p className="text-sm text-neutral-600 mt-2 max-w-lg">
                        Leads live here until a CV lands or they self-register. Then they auto-promote to the Candidate Pool and enter Kanban.
                    </p>
                </div>
                <button
                    onClick={() => setShowAdd(true)}
                    className="bg-ink text-white px-4 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-2 hover:bg-cobalt transition-colors"
                    data-testid="crm-add-open"
                >
                    <Plus size={14} weight="bold" /> Add lead
                </button>
            </div>

            {/* Section switch — Today (inbox-zero) vs Lead pool (raw list) */}
            <div className="inline-flex rounded-none border-2 border-ink overflow-hidden mb-6" data-testid="crm-section-tabs">
                <button
                    onClick={() => setSection("today")}
                    data-testid="crm-section-today"
                    className={`px-4 py-2 text-xs font-black uppercase tracking-wider transition inline-flex items-center gap-1 ${section === "today" ? "bg-ink text-paper" : "bg-white text-ink hover:bg-neutral-100"}`}
                >
                    <Sparkle size={12} weight="fill" /> Today
                </button>
                <button
                    onClick={() => setSection("pool")}
                    data-testid="crm-section-pool"
                    className={`px-4 py-2 text-xs font-black uppercase tracking-wider transition inline-flex items-center gap-1 border-l-2 border-ink ${section === "pool" ? "bg-ink text-paper" : "bg-white text-ink hover:bg-neutral-100"}`}
                >
                    <Users size={12} weight="bold" /> Lead pool
                </button>
            </div>

            {section === "today" ? (
                <TodayInbox testid="crm-today-inbox" />
            ) : (
            <>
            {showAdd && (
                <div className="mb-6">
                    <AddLeadForm
                        onCreated={() => { setShowAdd(false); fetchList(); }}
                        onCancel={() => setShowAdd(false)}
                    />
                </div>
            )}

            {/* Summary strip */}
            {summary && (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-6" data-testid="crm-summary">
                    {[
                        ["total", "Total"],
                        ["mine", "Mine"],
                        ["available", "Available"],
                        ["callbacks", "Callbacks"],
                        ["interested", "Interested"],
                    ].map(([k, l]) => (
                        <div key={k} className="border border-ink bg-white px-3 py-2">
                            <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">{l}</p>
                            <p className="font-heading font-black text-xl leading-none mt-1">{summary[k] ?? 0}</p>
                        </div>
                    ))}
                </div>
            )}

            {/* Tabs */}
            <div className="flex flex-wrap gap-1 mb-3" data-testid="crm-tabs">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    const active = view === t.key;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setView(t.key)}
                            data-testid={`crm-tab-${t.key}`}
                            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest border-2 flex items-center gap-1.5 transition-colors ${active ? "bg-ink text-white border-ink" : "border-neutral-300 text-neutral-700 hover:border-ink"}`}
                        >
                            <Icon size={12} weight="bold" /> {t.label}
                        </button>
                    );
                })}
            </div>

            {/* Filter row */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
                <div className="flex-1 min-w-[240px] border-2 border-ink flex items-center px-2 py-1.5 gap-2 bg-white">
                    <MagnifyingGlass size={14} weight="bold" className="text-neutral-500" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search name / email / phone…"
                        className="flex-1 outline-none text-sm bg-transparent"
                        data-testid="crm-search"
                    />
                </div>
                <select
                    value={sourceBatch}
                    onChange={(e) => setSourceBatch(e.target.value)}
                    className="border-2 border-ink px-2 py-1.5 text-xs font-bold bg-white"
                    data-testid="crm-source-filter"
                >
                    <option value="">All sources</option>
                    {SOURCE_BATCHES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
                <div className="flex border-2 border-ink" data-testid="crm-density-toggle">
                    <button onClick={() => setDensity("compact")} className={`px-2 py-1 text-[10px] font-bold uppercase ${density === "compact" ? "bg-ink text-white" : ""}`}>Compact</button>
                    <button onClick={() => setDensity("cozy")} className={`px-2 py-1 text-[10px] font-bold uppercase ${density === "cozy" ? "bg-ink text-white" : ""}`}>Cozy</button>
                </div>
            </div>

            <p className="text-xs text-neutral-500 mb-2">{total} lead{total === 1 ? "" : "s"} {loading && "· loading…"}</p>

            {/* V2.1.1 iter183 — sticky bulk-action bar (appears only when
                at least one lead is checked). Positioned below the tabs
                but above the row list so recruiters see it without
                scrolling back up. */}
            <BulkActionsBar
                selectedCount={selectedIds.size}
                onClear={() => setSelectedIds(new Set())}
                onReassign={() => setBulkModal("reassign")}
                onDisposition={() => setBulkModal("disposition")}
                onWhatsApp={() => setBulkModal("whatsapp")}
            />

            {/* Rows */}
            {error ? (
                <div data-testid="crm-error" className="border-2 border-rose-500 bg-rose-50 px-4 py-4">
                    <p className="text-sm font-bold text-rose-800">{error}</p>
                    <button onClick={fetchList} className="mt-2 text-xs font-bold uppercase bg-rose-800 text-white px-3 py-1">Retry</button>
                </div>
            ) : rows.length === 0 && !loading ? (
                <div data-testid="crm-empty" className="border border-dashed border-neutral-300 bg-neutral-50 px-6 py-16 text-center">
                    <p className="font-heading font-black text-lg">No leads yet on this view.</p>
                    <p className="text-xs text-neutral-500 mt-2">
                        Add a lead manually, import a CSV, or wait for the legacy Mongo pull.
                    </p>
                </div>
            ) : (
                <div className="border-2 border-ink bg-white divide-y divide-neutral-100" data-testid="crm-list">
                    {/* V2.1.1 iter183 — select-all header */}
                    <div className="px-4 py-2 bg-neutral-50 flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                        <input
                            type="checkbox"
                            data-testid="crm-select-all"
                            checked={rows.length > 0 && selectedIds.size === rows.length}
                            ref={(el) => {
                                if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < rows.length;
                            }}
                            onChange={toggleSelectAll}
                            className="cursor-pointer accent-cobalt w-4 h-4"
                        />
                        <span>
                            {selectedIds.size > 0
                                ? `${selectedIds.size} selected`
                                : `Select all on this page`}
                        </span>
                    </div>
                    {rows.map((r) => (
                        <div
                            key={r.id}
                            data-testid={`crm-row-${r.id.slice(0, 8)}`}
                            className={`px-4 ${rowH} flex items-center gap-3 hover:bg-neutral-50 cursor-pointer ${selectedIds.has(r.id) ? "bg-cobalt/5" : ""}`}
                            onClick={() => setOpenLeadId(r.id)}
                        >
                            <input
                                type="checkbox"
                                data-testid={`crm-row-check-${r.id.slice(0, 8)}`}
                                checked={selectedIds.has(r.id)}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => toggleSelect(r.id)}
                                className="cursor-pointer accent-cobalt w-4 h-4"
                            />
                            <PriorityDot code={r.current_disposition?.code} />
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                    <p className="font-bold text-sm text-ink truncate">{r.name || "—"}</p>
                                    <ActionChip lead={r} />
                                </div>
                                {density === "cozy" && (
                                    <p className="text-[11px] text-neutral-500 truncate mt-0.5">
                                        {r.current_title || "—"}{r.city ? ` · ${r.city}` : ""}
                                        {r.source_batch ? ` · ${r.source_batch}` : ""}
                                    </p>
                                )}
                            </div>
                            <span className="text-[10px] font-mono text-neutral-400 hidden sm:inline">
                                {_relTime(r.updated_at || r.created_at)}
                            </span>
                            <button
                                onClick={(e) => { e.stopPropagation(); setDispTarget(r.id); }}
                                data-testid={`crm-disposition-btn-${r.id.slice(0, 8)}`}
                                className="text-xs font-bold uppercase text-cobalt hover:text-ink flex items-center gap-1"
                            >
                                Disposition <ArrowRight size={12} weight="bold" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
            </>
            )}

            <DispositionModal
                open={!!dispTarget}
                onClose={() => setDispTarget(null)}
                targetType="crm_lead"
                targetId={dispTarget}
                onSubmitted={() => { fetchList(); fetchSummary(); }}
            />
            <CrmLeadDrawer
                leadId={openLeadId}
                open={!!openLeadId}
                onClose={() => setOpenLeadId(null)}
                onPromoted={() => { fetchList(); fetchSummary(); }}
                onDispositionRequest={(id) => { setDispTarget(id); }}
            />

            {/* V2.1.1 iter183 — bulk modals */}
            <BulkReassignModal
                open={bulkModal === "reassign"}
                leadIds={[...selectedIds]}
                onClose={() => setBulkModal(null)}
                onDone={() => {
                    setBulkModal(null);
                    setSelectedIds(new Set());
                    fetchList();
                    fetchSummary();
                }}
            />
            <BulkDispositionModal
                open={bulkModal === "disposition"}
                leadIds={[...selectedIds]}
                onClose={() => setBulkModal(null)}
                onDone={() => {
                    setBulkModal(null);
                    setSelectedIds(new Set());
                    fetchList();
                    fetchSummary();
                }}
            />
            <BulkWhatsAppModal
                open={bulkModal === "whatsapp"}
                leadIds={[...selectedIds]}
                onClose={() => setBulkModal(null)}
                onDone={() => {
                    setBulkModal(null);
                    setSelectedIds(new Set());
                    fetchList();
                    fetchSummary();
                }}
            />
        </div>
    );
}
